import axios from "axios";
import Cookies from "js-cookie";
import { ENCRYPTION_OF_DATA } from "helper/Functions";
import { API_URL } from "helper/Constants";

class UserService {
  constructor(baseURL) {
    this.api = axios.create({
      baseURL: baseURL || API_URL,
    });

    this.lastModified = null;

    // Request Interceptor
    this.api.interceptors.request.use(
      async (config) => {
        const token = Cookies.get("token");
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        if (config.data) {
          config.headers["Content-Type"] = `application/json`;
          config.data = await ENCRYPTION_OF_DATA(config.data);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response Interceptor
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response &&
          error.response.status === 401 &&
          error.response.data.message !== "Invalid refresh token"
        ) {
          try {
            const newToken = await this.refreshToken();
            if (newToken) {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            window?.location?.reload();
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async refreshToken() {
    try {
      const response = await this.api.post("/auth/refresh-token", {}, { withCredentials: true });
      const newAccessToken = response.data.accessToken;
      Cookies.set("token", newAccessToken, { secure: true, sameSite: "None" });
      return newAccessToken;
    } catch (error) {
      Cookies.remove("user", { secure: true, sameSite: "None" });
      Cookies.remove("token", { secure: true, sameSite: "None" });
      Cookies.remove("refreshToken", { secure: true, sameSite: "None" });
      window?.location?.reload();
      console.error("Failed to refresh token:", error);
      return null;
    }
  }

  async getUserRoles(userId, role) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.get(`/usd/getRolesBelow`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      return response.data || [];
    } catch (error) {
      console.error("Error fetching roles:", error.response || error);
      throw new Error("Failed to fetch roles.");
    }
  }

  async updateUserStatus(userId, newStatus) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.patch(
        `/usb/${userId}/status`,
        { status: newStatus },
        {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      console.error("API Error - updateUserStatus:", error.response?.data || error.message);
      throw error.response?.data || { message: "Failed to update user status." };
    }
  }

  async getUserBalance(dto) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.get(`/usb/user-balance`, {
        params: dto,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      return response.data || [];
    } catch (error) {
      console.error("Error fetching balance:", error.response || error);
      throw new Error(error?.response?.data?.message ?? "Failed to fetch balance.");
    }
  }

  // NEW: Get current settings (global balance limit & telegram config)
  async getGlobalSettings() {
    try {
      const token = Cookies.get("token");
      const response = await this.api.get(`/usb/getSettings`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching settings:", error.response || error);
      throw new Error("Failed to fetch settings.");
    }
  }

  // NEW: Update global balance limit
  async updateGlobalBalanceLimit(limitValue) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.put(
        `/usb/global-balance-limit`,
        { value: limitValue },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating balance limit:", error.response || error);
      throw new Error("Failed to update global balance limit.");
    }
  }

  async getUserBalanceLimit(userId) {
  try {
    const response = await this.api.get(`/usb/user/${userId}/balance-limit`);
    return response.data; // return actual payload
  } catch (error) {
    console.error("Error fetching user balance limit:", error);
    throw error.response?.data || error;
  }
}

async updateUserBalanceLimit(userId, payload) {
  try {
    const token = Cookies.get("token");
    const response = await this.api.put(`/usb/user/${userId}/balance-limit`, payload,{
      method: 'POST',
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
    });
    return response.data; // return success message or updated limit
  } catch (error) {
    console.error("Error updating user balance limit:", error);
    throw error.response?.data || error;
  }
}

async getOnlineUserBalance(dto) {
    try {
      const token = Cookies.get("token");

      const response = await this.api.get(`/usb/online-user-balance`, {
        params: dto,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data || [];
    } catch (error) {
      console.error("Error fetching transaction history:", error.response || error);
      throw new Error("Failed to fetch transaction history.");
    }
  };

  // NEW: Update Telegram Config
  async updateTelegramConfig(botId, chatId) {
    try {
      const token = Cookies.get("token");
      const response = await this.api.put(
        `/usb/telegram-config`,
        { botId, chatId },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating Telegram config:", error.response || error);
      throw new Error("Failed to update Telegram config.");
    }
  }
}

export default UserService;
